/**
 * Build Review service
 * Browser-local demo state for build/test/review/approval workflow.
 */

import { formatDistanceToNow } from 'date-fns';
import type { TeamRole } from '../types';

const STORAGE_KEY = 'business-ai-build-review-v1';

export type BuildRunStatus = 'passed' | 'running' | 'failed' | 'blocked';
export type BuildRunStage = 'build' | 'test' | 'deploy';
export type QASeverity = 'blocker' | 'warning' | 'suggestion';
export type ReviewStatus = 'draft' | 'review' | 'approved' | 'fix_required' | 'retest';
export type LinearState = 'Todo' | 'In Progress' | 'In Review' | 'Done';

export interface BuildRun {
  id: string;
  label: string;
  branch: string;
  commit: string;
  actor: string;
  stage: BuildRunStage;
  status: BuildRunStatus;
  duration: string;
  finishedAt: string;
  summary: string;
}

export interface QAInboxItem {
  id: string;
  title: string;
  severity: QASeverity;
  source: 'e2e' | 'visual_regression' | 'manual_qa' | 'analytics';
  area: string;
  ownerRole: TeamRole;
  summary: string;
  linkedRunId: string;
  linkedIssueId: string;
}

export interface ReviewCard {
  id: string;
  title: string;
  status: ReviewStatus;
  owner: string;
  reviewer: string;
  roleHint: TeamRole;
  scope: string;
  linkedIssueId: string;
  updatedAt: string;
}

export interface LinearSyncItem {
  id: string;
  issueId: string;
  title: string;
  state: LinearState;
  syncedAt: string;
  owner: string;
  branch: string;
}

export interface BuildReviewState {
  runs: BuildRun[];
  qaInbox: QAInboxItem[];
  reviews: ReviewCard[];
  linearSync: LinearSyncItem[];
  selectedRole: TeamRole;
}

const DEFAULT_STATE: BuildReviewState = {
  selectedRole: 'team_lead',
  runs: [
    {
      id: 'run-1042',
      label: 'web-release-1042',
      branch: 'release/build-review-polish',
      commit: '8f3d21a',
      actor: 'Jiyoung Lee',
      stage: 'deploy',
      status: 'failed',
      duration: '9m 12s',
      finishedAt: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
      summary: 'Production deploy halted after checkout KPI card overflowed on iPhone SE.',
    },
    {
      id: 'run-1041',
      label: 'qa-smoke-1041',
      branch: 'feat/mobile-checkout-fixes',
      commit: '7bc4d90',
      actor: 'Junho Choi',
      stage: 'test',
      status: 'running',
      duration: '4m 44s',
      finishedAt: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
      summary: 'Smoke tests are re-running after payment CTA spacing fix.',
    },
    {
      id: 'run-1040',
      label: 'build-1040',
      branch: 'feat/mobile-checkout-fixes',
      commit: '6ab812c',
      actor: 'Yujin Jung',
      stage: 'build',
      status: 'passed',
      duration: '2m 31s',
      finishedAt: new Date(Date.now() - 1000 * 60 * 52).toISOString(),
      summary: 'Build passed with bundle size within threshold after image lazy-load patch.',
    },
    {
      id: 'run-1039',
      label: 'e2e-1039',
      branch: 'feat/mobile-checkout-fixes',
      commit: '65cd902',
      actor: 'Minsu Kim',
      stage: 'test',
      status: 'failed',
      duration: '7m 05s',
      finishedAt: new Date(Date.now() - 1000 * 60 * 110).toISOString(),
      summary: 'Checkout coupon path failed on Safari due to stale cache hydration.',
    },
  ],
  qaInbox: [
    {
      id: 'qa-1',
      title: 'Coupon drawer overlaps payment CTA on small screens',
      severity: 'blocker',
      source: 'manual_qa',
      area: 'Checkout / Mobile web',
      ownerRole: 'developer',
      summary: 'Blocks release because users cannot confirm payment on 375px viewport.',
      linkedRunId: 'run-1042',
      linkedIssueId: 'VICE-318',
    },
    {
      id: 'qa-2',
      title: 'Review badge count lags by one refresh after retest',
      severity: 'warning',
      source: 'analytics',
      area: 'Build Review',
      ownerRole: 'planner',
      summary: 'Does not block ship, but weakens trust in board freshness.',
      linkedRunId: 'run-1041',
      linkedIssueId: 'VICE-322',
    },
    {
      id: 'qa-3',
      title: 'Add screenshot diff preview directly from QA inbox',
      severity: 'suggestion',
      source: 'visual_regression',
      area: 'QA Workflow',
      ownerRole: 'qa',
      summary: 'Would shorten handoff time between QA and developer for visual regressions.',
      linkedRunId: 'run-1039',
      linkedIssueId: 'VICE-325',
    },
  ],
  reviews: [
    {
      id: 'review-1',
      title: 'Mobile checkout spacing and sticky CTA guardrail',
      status: 'fix_required',
      owner: 'Yujin Jung',
      reviewer: 'Junho Choi',
      roleHint: 'developer',
      scope: 'Checkout / Payments',
      linkedIssueId: 'VICE-318',
      updatedAt: new Date(Date.now() - 1000 * 60 * 14).toISOString(),
    },
    {
      id: 'review-2',
      title: 'Regression-proof review counters for release board',
      status: 'review',
      owner: 'Seohyun Park',
      reviewer: 'Minsu Kim',
      roleHint: 'planner',
      scope: 'Operations / Build Review',
      linkedIssueId: 'VICE-322',
      updatedAt: new Date(Date.now() - 1000 * 60 * 32).toISOString(),
    },
    {
      id: 'review-3',
      title: 'Visual diff preview handoff improvements',
      status: 'draft',
      owner: 'Junho Choi',
      reviewer: 'Jiyoung Lee',
      roleHint: 'qa',
      scope: 'QA Inbox',
      linkedIssueId: 'VICE-325',
      updatedAt: new Date(Date.now() - 1000 * 60 * 80).toISOString(),
    },
    {
      id: 'review-4',
      title: 'Release note auto-linking for shipped fixes',
      status: 'approved',
      owner: 'Minsu Kim',
      reviewer: 'Seohyun Park',
      roleHint: 'team_lead',
      scope: 'Insights / Notes',
      linkedIssueId: 'VICE-301',
      updatedAt: new Date(Date.now() - 1000 * 60 * 200).toISOString(),
    },
  ],
  linearSync: [
    {
      id: 'linear-1',
      issueId: 'VICE-318',
      title: 'Fix mobile checkout CTA collision',
      state: 'In Progress',
      syncedAt: new Date(Date.now() - 1000 * 60 * 11).toISOString(),
      owner: 'Yujin Jung',
      branch: 'feat/mobile-checkout-fixes',
    },
    {
      id: 'linear-2',
      issueId: 'VICE-322',
      title: 'Stabilize board counters after retest',
      state: 'In Review',
      syncedAt: new Date(Date.now() - 1000 * 60 * 27).toISOString(),
      owner: 'Seohyun Park',
      branch: 'feat/review-counter-freshness',
    },
    {
      id: 'linear-3',
      issueId: 'VICE-325',
      title: 'Expose screenshot diff preview in QA inbox',
      state: 'Todo',
      syncedAt: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
      owner: 'Junho Choi',
      branch: 'discovery/qa-preview-panel',
    },
  ],
};

function cloneState(state: BuildReviewState): BuildReviewState {
  return JSON.parse(JSON.stringify(state));
}

function loadState(): BuildReviewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return cloneState(DEFAULT_STATE);
    const parsed = JSON.parse(raw) as BuildReviewState;
    return {
      ...cloneState(DEFAULT_STATE),
      ...parsed,
    };
  } catch {
    return cloneState(DEFAULT_STATE);
  }
}

function saveState(state: BuildReviewState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getBuildReviewState(workspaceId?: string): BuildReviewState {
  void workspaceId;
  return loadState();
}

export function updateSelectedBuildReviewRole(role: TeamRole): BuildReviewState {
  const state = loadState();
  state.selectedRole = role;
  saveState(state);
  return state;
}

export function updateReviewStatus(reviewId: string, status: ReviewStatus): BuildReviewState {
  const state = loadState();
  state.reviews = state.reviews.map((review) =>
    review.id === reviewId ? { ...review, status, updatedAt: new Date().toISOString() } : review
  );
  saveState(state);
  return state;
}

export function promoteQAItem(itemId: string): BuildReviewState {
  const state = loadState();
  const item = state.qaInbox.find((entry) => entry.id === itemId);
  if (!item) return state;

  state.reviews = [
    {
      id: `review-${Date.now()}`,
      title: item.title,
      status: 'review',
      owner: roleToOwner(item.ownerRole),
      reviewer: 'Minsu Kim',
      roleHint: item.ownerRole,
      scope: item.area,
      linkedIssueId: item.linkedIssueId,
      updatedAt: new Date().toISOString(),
    },
    ...state.reviews,
  ];
  state.qaInbox = state.qaInbox.filter((entry) => entry.id !== itemId);
  saveState(state);
  return state;
}

export function markRunStatus(runId: string, status: BuildRunStatus): BuildReviewState {
  const state = loadState();
  state.runs = state.runs.map((run) =>
    run.id === runId
      ? {
          ...run,
          status,
          finishedAt: new Date().toISOString(),
          summary:
            status === 'passed'
              ? 'Run manually marked as healthy and ready for the next review gate.'
              : status === 'failed'
                ? 'Run manually marked failed for follow-up investigation.'
                : run.summary,
        }
      : run
  );
  saveState(state);
  return state;
}

export function getBuildReviewMetrics(state: BuildReviewState) {
  return {
    activeRuns: state.runs.filter((run) => run.status === 'running').length,
    blockers: state.qaInbox.filter((item) => item.severity === 'blocker').length,
    approvals: state.reviews.filter((review) => review.status === 'approved').length,
    syncCoverage: `${state.linearSync.length}/${state.reviews.length} linked`,
  };
}

export function getRoleActionHints(role: TeamRole) {
  const hints: Record<TeamRole, { title: string; actions: string[] }> = {
    team_lead: {
      title: 'Ship only when blockers are burned down and approvals are traceable.',
      actions: ['Review blockers first', 'Check deploy risk before approval', 'Confirm Linear sync is fresh'],
    },
    developer: {
      title: 'Convert failing runs into retest-ready fixes with clear issue linkage.',
      actions: ['Fix blocker from QA inbox', 'Push review card to retest', 'Re-run failed build/test flows'],
    },
    planner: {
      title: 'Keep scope tight and make review handoffs legible.',
      actions: ['Validate linked Linear issues', 'Split warnings from ship blockers', 'Update review scope and priorities'],
    },
    qa: {
      title: 'Protect release quality by turning observations into reproducible checks.',
      actions: ['Triage manual QA findings', 'Promote verified issues to review', 'Tag retest candidates with evidence'],
    },
    member: {
      title: 'Follow the board state and unblock the highest-signal task.',
      actions: ['Pick a review card in review', 'Watch the latest failed run', 'Sync context from linked Linear issue'],
    },
  };

  return hints[role];
}

export function formatRelativeTime(value: string) {
  return formatDistanceToNow(new Date(value), { addSuffix: true });
}

export function getBuildReviewStats() {
  const state = loadState();
  const latestRun = state.runs[0];
  const blockers = state.qaInbox.filter(item => item.severity === 'blocker').length;
  const linkedCount = state.linearSync.length;
  const totalReviews = state.reviews.length;
  
  return {
    latestRunStatus: latestRun?.status || 'passed',
    openBlockers: blockers,
    syncCoverage: totalReviews > 0 ? Math.round((linkedCount / totalReviews) * 100) : 100,
    activeRuns: state.runs.filter(run => run.status === 'running').length,
  };
}

function roleToOwner(role: TeamRole) {
  switch (role) {
    case 'developer':
      return 'Yujin Jung';
    case 'planner':
      return 'Seohyun Park';
    case 'qa':
      return 'Junho Choi';
    case 'team_lead':
      return 'Minsu Kim';
    default:
      return 'Delivery Team';
  }
}
